import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { transition } from "../../utils/transition";
import { fadeIn } from "../../utils/variants";

const BRAND = { navy: "#062b6c", green: "#00ad0f" };

const galleryItems = [
  {
    id: "glr1",
    src: "/new_img/3rd-party-inspection.webp",
    label: "3rd Party Statutory Inspection",
    category: "Inspection",
  },
  {
    id: "glr2",
    src: "/new_img/equipment-leasing.webp",
    label: "Equipment Leasing",
    category: "Support Services",
  },
  {
    id: "glr3",
    src: "/new_img/equipment-maintenance.webp",
    label: "Equipment Maintenance",
    category: "Maintenance",
  },
  {
    id: "glr4",
    src: "/new_img/highrise-cleaning.webp",
    label: "High-Rise Cleaning Operations",
    category: "Access & Safety",
  },
  {
    id: "glr5",
    src: "/new_img/safety-support.png",
    label: "Safety Support & Supervision",
    category: "HSE",
  },
  {
    id: "glr6",
    src: "/new_img/scaffolding.webp",
    label: "Scaffolding & Access Engineering",
    category: "Scaffolding",
  },
  {
    id: "glr7",
    src: "/new_img/working-exhaust.jpeg",
    label: "Confined Space & Exhaust Work",
    category: "Operations",
  },
  {
    id: "glr8",
    src: "/new_img/welding-and-fabrication.png",
    label: "Welding & Fabrication",
    category: "Fabrication",
  },
];

const Gallery = () => {
  const [activeItem, setActiveItem] = useState(null);

  // Close on ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setActiveItem(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="bg-[#0f172a] text-slate-50 min-h-screen">
      {/* HEADER / INTRO – very different layout */}
      <section className="relative pt-24 pb-10 md:pt-28 md:pb-12">
        <div className="mx-auto max-w-6xl px-5">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
                Field Operations • Visual Log
              </p>
              <h1 className="mt-2 text-3xl font-semibold md:text-4xl">
                Scaftech Operations Gallery
              </h1>
              <p className="mt-3 max-w-xl text-sm text-slate-300 md:text-[0.96rem]">
                A visual snapshot of real projects, inspections, maintenance
                work, and access solutions delivered across our operational
                footprint. Every frame reflects our commitment to safety,
                precision, and technical excellence.
              </p>
            </div>

            <div className="mt-2 flex flex-wrap gap-3 md:justify-end">
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-slate-300">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: BRAND.green }}
                />
                Live Project Footage
              </span>
              <span className="inline-flex items-center rounded-full bg-slate-800 px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-slate-200">
                Curated by Scaftech
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* GRID – new layout concept */}
      <section className="pb-16">
        <div className="mx-auto max-w-6xl px-5">
          {/* simple legend row */}
          <div className="mb-6 flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
            <span className="uppercase tracking-[0.24em]">Categories:</span>
            <span className="rounded-full bg-slate-800/80 px-3 py-1">
              Inspection
            </span>
            <span className="rounded-full bg-slate-800/80 px-3 py-1">
              Access &amp; Safety
            </span>
            <span className="rounded-full bg-slate-800/80 px-3 py-1">
              Maintenance &amp; Fabrication
            </span>
          </div>

          {/* staggered grid – not like your old 2+1 layout */}
          <motion.div
            variants={fadeIn("Up")}
            transition={transition()}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false }}
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {galleryItems.map((item, index) => {
              const tall = index % 5 === 0; // just to mix heights
              return (
                <motion.button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveItem(item)}
                  whileHover={{ scale: 1.01 }}
                  className={`group relative overflow-hidden rounded-3xl border border-slate-700/70 bg-slate-900/60 text-left shadow-[0_18px_45px_rgba(0,0,0,0.7)] transition-all ${
                    tall ? "row-span-2 h-80" : "h-56"
                  }`}
                >
                  <img
                    src={item.src}
                    alt={item.label}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* gradient + label */}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                  <div className="absolute inset-x-4 bottom-4 flex flex-col gap-1">
                    <span className="inline-flex w-fit items-center rounded-full bg-black/60 px-2 py-[2px] text-[10px] uppercase tracking-[0.22em] text-slate-200">
                      {item.category}
                    </span>
                    <p className="text-sm font-medium text-slate-50">
                      {item.label}
                    </p>
                  </div>

                  {/* corner accent */}
                  <span
                    className="absolute right-4 top-4 h-2 w-2 rounded-full shadow-[0_0_12px_rgba(34,197,94,0.9)]"
                    style={{ background: BRAND.green }}
                  />
                </motion.button>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* LIGHTBOX / MODAL PREVIEW */}
      <AnimatePresence>
        {activeItem && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveItem(null)}
          >
            <motion.div
              className="relative max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-3xl bg-slate-950 shadow-[0_24px_80px_rgba(0,0,0,0.9)]"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 180, damping: 18 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img
                  src={activeItem.src}
                  alt={activeItem.label}
                  className="max-h-[70vh] w-full object-contain bg-black"
                />

                <button
                  type="button"
                  onClick={() => setActiveItem(null)}
                  className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-sm text-slate-200 hover:bg-black/80"
                >
                  ✕
                </button>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-slate-800 bg-slate-950/90 px-5 py-3 text-[12px] text-slate-200">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase tracking-[0.24em] text-slate-400">
                    Scaftech Field Capture
                  </span>
                  <span className="text-[13px] font-medium">
                    {activeItem.label}
                  </span>
                  <span className="text-[11px] text-slate-400">
                    Category: {activeItem.category}
                  </span>
                </div>
                <span
                  className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-emerald-200"
                  style={{ backgroundColor: `${BRAND.navy}` }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ background: BRAND.green }}
                  />
                  Scaftech
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
